import { useState, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Context } from '../../../Context';
import { ApartmentViewContext } from '../context/ApartmentViewContext';
import CustomToolTip from '../../ToolTipInfoCustomMUI';
import Zoom from '@mui/material/Zoom';
// import { ReactComponent as IconSale } from '../../media/img/icon-sale.svg';
import { ReactComponent as IconDocuments } from '../../../media/img/icon-documents.svg';
import { ReactComponent as IconCar } from '../../../media/img/icon-car.svg';
import { ReactComponent as IconHistory } from '../../../media/img/icon-history.svg';
import { ReactComponent as IconChevron } from '../../../media/img/icon-chevron.svg';
import bim_styles from '../../../styles/bim.module.scss';
import apart_styles from '../../../styles/aprt.module.scss';
import { use } from 'react';
import ApartmentQueryButton from './ApartmentQueryButton';

function PriceInfoBlock({ isStuck }) {
	const { projectSetup, projectStyle, activeApartmentData, isMobile } =
		useContext(Context);
	const { setInfoTooltipData } = useContext(ApartmentViewContext);

	const { t } = useTranslation();

	const [expandedPriceBlock, setExpandedPriceBlock] = useState(true);

	const storedSupplementsChoices =
		JSON.parse(localStorage.getItem('selectedSupplements')) || {};
	const selectedParkingSpaces =
		storedSupplementsChoices[activeApartmentData?.uuid]?.parkingSpaces || [];
	const selectedStorageUnits =
		storedSupplementsChoices[activeApartmentData?.uuid]?.storageUnits || [];

	const additionalInfoIcons = {
		supplementOptionsIcon:
			projectSetup?.additional_unit_information?.supplement_purchases_options
				?.is_visible || false,
		monetaryObligationsIcon:
			projectSetup?.additional_unit_information?.additional_monetary_obligations
				?.is_visible || false,
		priceHistoryIcon:
			projectSetup?.additional_unit_information?.unit_price_history
				?.is_visible || true,
	};
	const querySupplementaryAreasConfig = projectSetup?.units?.query_supplementary_areas || {};
	const querySupplementaryAreas = Object.keys(querySupplementaryAreasConfig).length || 0;

	// const isOnOffer = activeApartmentData?.price_offer?.on_offer && activeApartmentData?.price_history?.length > 1;
	const isOnOffer = activeApartmentData?.price_offer?.on_offer;
	const offerDate = activeApartmentData?.price_offer?.date || null;
	const currentPrice = activeApartmentData?.price_brutto || null;
	const currentPriceSqm = activeApartmentData?.price_m2 || null;
	const priceHistory = activeApartmentData?.price_history || [];
	const basePrice = priceHistory[0]?.price_brutto || null;
	const vatRate = activeApartmentData?.price_vat || null;

	const didPriceChange = currentPrice !== basePrice;

	const priceHistoryWithSqm = priceHistory.map((entry) => {
		return {
			...entry,
			price_m2: entry.price_brutto / (activeApartmentData?.area_original || 1),
		};
	});

	const getLowestPriceIn30DaysBeforeOffer = () => {
		if (!priceHistory || priceHistory.length === 0) return currentPrice;

		const reducedPriceTimestamp = new Date(
			priceHistory[priceHistory.length - 1].date
		).getTime();

		const thirtyDaysBeforeOffer =
			reducedPriceTimestamp - 31 * 24 * 60 * 60 * 1000;

		const pricesInPeriod = priceHistory.filter((entry) => {
			const entryTimestamp = new Date(entry.date).getTime();
			return (
				entryTimestamp >= thirtyDaysBeforeOffer &&
				entryTimestamp < reducedPriceTimestamp
			);
		});

		//ostatnia zmianę ceny przed początkiem okresu
		const previousEntries = priceHistory.filter((entry) => {
			const entryTimestamp = new Date(entry.date).getTime();
			return entryTimestamp < thirtyDaysBeforeOffer;
		});
		let lastBeforePeriod = null;
		if (previousEntries.length > 0) {
			lastBeforePeriod = previousEntries.reduce((latest, entry) => {
				const entryTimestamp = new Date(entry.date).getTime();
				return entryTimestamp > new Date(latest.date).getTime()
					? entry
					: latest;
			}, previousEntries[0]);
		} else {
			lastBeforePeriod = priceHistory[0];
		}

		const allRelevantPrices = lastBeforePeriod
			? [lastBeforePeriod, ...pricesInPeriod]
			: pricesInPeriod;

		if (allRelevantPrices.length === 0) return priceHistory[0].price_brutto;
		return Math.min(...allRelevantPrices.map((entry) => entry.price_brutto));
	};

	const getAbbreviatedPriceValue = (value) => {
		if (value >= 1_000_000) {
			return `${(value / 1_000_000).toLocaleString('pl-PL', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			})} mln`;
		}
		return `${(value / 1000).toLocaleString('pl-PL', {
			maximumFractionDigits: 0,
		})} tys.`;
	};

	const getDisplayPrice = (num, digits = 2) => {
		if (num === null || num === undefined) return '-';

		return parseFloat(num).toLocaleString('pl-PL', {
			style: 'currency',
			currency: 'PLN',
			minimumFractionDigits: digits,
			maximumFractionDigits: digits,
		});
	};

	const getDisplaySupplementPrice = (priceRange) => {
		if (!priceRange || priceRange.length === 0) return '-';

		if (priceRange.length === 1) return getDisplayPrice(priceRange[0], 0);

		const formattedPriceRange = priceRange
			.map((price) => getAbbreviatedPriceValue(price))
			.join(' - ');
		return formattedPriceRange + ' zł';
	};

	/*
	<Select
  styles={{
    control: (baseStyles, state) => ({
      ...baseStyles,
      borderColor: state.isFocused ? 'grey' : 'red',
    }),
  }}
/>
	*/
	const customStyles = {
		control: (provided) => ({
			...provided,
			borderRadius: '0',
		}),
	};

	const jointOfferSupplements = querySupplementaryAreas
		? [
		//get from data
		{
			type: 'parking',
			name: 'idParkingSpaceWithStorageBox',
			priceRange: [55000],
			name_original: 'MPB01',
			number: 'MPB01',
		},
		{
			type: 'storage',
			name: 'idStorageUnit',
			priceRange: [10000],
			name_original: 'KM21',
			number: 'KM21',
		},
	]
	: [];

	const getUserAddedSupplements = () => {
		// temporary function to get user sample choices
		const chosenSupplements = [];
		if (selectedParkingSpaces[0]) {
			// for now this is a number
			chosenSupplements.push({
				type: 'parking',
				name: 'idParkingSpaceOutdoor',
				priceRange: [30000, 35000],
			});
		}
		if (selectedStorageUnits[0]) {
			// for now this is a number
			chosenSupplements.push({
				type: 'storage',
				name: 'idStorageUnit',
				priceRange: [10000, 15000],
			});
		}
		return chosenSupplements;
	};

	const userAddedSupplements = querySupplementaryAreas ? getUserAddedSupplements() : [];

	const addedSupplements = [
		//
		...jointOfferSupplements,
		...userAddedSupplements,
	];

	const areAddedSupplementsPriceRanged = addedSupplements.some(
		(sup) => sup.priceRange.length > 1
	);
	const sumSupsPrice = addedSupplements
		.map((sup) => sup.priceRange[0])
		.reduce((partialSum, a) => partialSum + a, 0);

	const numberOfUserAddedStorageUnits = selectedStorageUnits[0]; //temporary solution cause for now we keep a number in localStorage
	const numberOfUserAddedParkingSpaces = selectedParkingSpaces[0]; //temporary solution cause for now we keep a number in localStorage

	// console.log('numberOfUserAddedStorageUnits', activeApartmentData.uuid, activeApartmentData.id_original, numberOfUserAddedStorageUnits)
	// console.log('numberOfUserAddedParkingSpaces', activeApartmentData.uuid, activeApartmentData.id_original, numberOfUserAddedParkingSpaces)

	useEffect(() => {
		// setExpandedPriceBlock(isStuck ? false : true);
		setExpandedPriceBlock(true);
	}, [
		activeApartmentData,
		// isStuck
	]);

	return (
		<>
			{isOnOffer && (
				<div style={{}}>
					<span //h4
						style={{
							padding: '0.25rem 0.75rem',
							backgroundColor: 'var(--color-headings)',
							color: 'var(--color-white)',
							fontSize: '1.2rem',
							lineHeight: '1.5',
							fontWeight: '600',
							fontFamily: 'var(--font-headings)',
						}}
					>
						{t('on_offer')}
					</span>
				</div>
			)}
			<div
				style={{
					// width: '100%',
					display: 'flex',
					flexDirection: 'column',
					transition: 'box-shadow 300ms ease-in-out',
					boxShadow: isStuck ? '0 4px 12px rgba(0, 0, 0, 0.2)' : 'none',
					marginBottom: '1rem',
				}}
			>
				<div
					// className={bim_styles.apartment_tile_price_container}
					style={{
						width: '100%',
						padding: '0 1rem',
						backgroundColor: 'var(--color-offwhite)',
					}}
				>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							flexDirection: 'column',
						}}
					>
						<div // ---------------------- pudełko na tytuł i ikony -----------------------
							style={{
								width: '100%',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								padding: '1rem 0',
								// borderBottom: '1px solid var(--color-labels)',
							}}
						>
							<span //h5
								style={{
									fontWeight: '600',
									fontSize: '0.9rem',
									color: 'var(--color-labels)',
									fontFamily: 'var(--font-headings)',
								}}
							>
								{t('prices')}
							</span>
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '0.5rem',
									justifyContent: 'flex-end',
								}}
							>
								{additionalInfoIcons.supplementOptionsIcon && (
									<CustomToolTip
										title={
											<>
												<p className={apart_styles.infoTooltip__paragraph}>
													Zakup opcjonalny miejsc postojowych i komórek
													lokatorskich w cenach:
												</p>
												<p className={apart_styles.infoTooltip__paragraph}>
													Miejsce postojowe naziemne: 30 000 zł
													<br/>Miejsce parkingowe w garażu podziemnym: 55 - 65 000 zł
												</p>
												<p className={apart_styles.infoTooltip__paragraph}>
													Komórki lokatorskie: 15 000 zł
												</p>
											</>
										}
										slots={{
											transition: Zoom,
										}}
										arrow
									>
										<IconCar style={{ width: '1.5rem', height: '1.5rem' }} />
									</CustomToolTip>
								)}
								{additionalInfoIcons.monetaryObligationsIcon && (
									<CustomToolTip
										title={
											<>
												<p className={apart_styles.infoTooltip__paragraph}>
													Inne świadczenia pieniężne
												</p>
												{/* <p className={apart_styles.infoTooltip__paragraph}>
													Począwszy od dnia 1 stycznia roku następującego po
													roku, w którym zawarta zostanie umowa przyrzeczona,
													Nabywca zobowiązany będzie do uiszczania opłaty z
													tytułu przekształcenia prawa użytkowania wieczystego
													gruntu wchodzącego w skład nieruchomości w prawo
													własności na zasadach określonych w ustawie z dnia 20
													lipca 2018 roku o przekształceniu prawa użytkowania
													wieczystego gruntów zabudowanych na cele mieszkaniowe
													w prawo własności tych gruntów.
													<br />
													Nabywca zobowiązany będzie ponadto - najpóźniej w
													terminie do dnia zawarcia umowy przyrzeczonej - do
													uiszczenia deweloperowi przypadającej na Nabywcę
													części tej opłaty z tytułu udziału Nabywcy w
													nieruchomości wspólnej, w takiej samej proporcji, jaką
													stanowi ilość dni od dnia zawarcia umowy przyrzeczonej
													do końca roku kalendarzowego, w którym umowa
													przyrzeczona zostanie zawarta, do wszystkich dni w
													roku kalendarzowym, w którym umowa przyrzeczona
													zostanie zawarta.
												</p> */}
												<p className={apart_styles.infoTooltip__paragraph}>
													Inne świadczenia, o których mowa w art. 19a ust. 1 pkt
													3) ustawy z dnia 21 maja 2025 roku o zmianie ustawy o
													ochronie praw nabywcy lokalu mieszkalnego lub domu
													jednorodzinnego oraz Deweloperskim Funduszu
													Gwarancyjnym:
													<br /> • koszty aktów notarialnych i opłat sądowych
													<br /> • koszty zmian aranżacyjnych/programów
													wykończeniowych wg indywidualnego kosztorysu
													<br /> • koszty zarządzania i administrowania
													częściami wspólnymi
													<br /> • koszty eksploatacji i utrzymania lokalu oraz
													praw związanych
													<br /> • koszty związane z cesją praw i obowiązków na
													innego nabywcę
												</p>
											</>
										}
										slots={{
											transition: Zoom,
										}}
										arrow
									>
										<IconDocuments
											style={{ width: '1.5rem', height: '1.5rem' }}
										/>
									</CustomToolTip>
								)}
								{(additionalInfoIcons.priceHistoryIcon &&
									priceHistoryWithSqm.length > 0) && (
										<CustomToolTip
											title={
												<>
													<p className={apart_styles.infoTooltip__paragraph}>
														{t('price_history')}
													</p>
													<div
														style={{
															display: 'grid',
															gridTemplateColumns:
																'repeat(3, minmax(6rem, 1fr))',
															columnGap: '0.5rem',
														}}
													>
														<div>{t('price_of_sqm')}:</div>
														<div>{t('price_total')}:</div>
														<div>{t('price_since')}:</div>
														{priceHistoryWithSqm.map((entry, index) => {
															const date = entry.date
																? new Date(entry.date).toLocaleString('pl-PL', {
																		year: 'numeric',
																		month: '2-digit',
																		day: '2-digit',
																  })
																: '-';
															return (
																<>
																	<div key={index * priceHistoryWithSqm.length}>
																		{getDisplayPrice(entry.price_m2)}
																	</div>
																	<div
																		key={index * priceHistoryWithSqm.length + 1}
																	>
																		{getDisplayPrice(entry.price_brutto)}
																	</div>
																	<div
																		key={index * priceHistoryWithSqm.length + 2}
																	>
																		{date}
																	</div>
																</>
															);
														})}
													</div>
												</>
											}
											slots={{
												transition: Zoom,
											}}
											arrow
										>
											<IconHistory
												style={{ width: '1.5rem', height: '1.5rem' }}
											/>
										</CustomToolTip>
									)}
						{(querySupplementaryAreas || addedSupplements.length > 0) && (
							<CustomToolTip
									title={
										expandedPriceBlock
											? 'Minimalizuj blok cenowy'
											: 'Rozwiń blok cenowy'
									}
									slots={{
										transition: Zoom,
									}}
									arrow
								>
									<IconChevron
										style={{
											width: '1.5rem',
											height: '1.5rem',
											cursor: 'pointer',
											transform: expandedPriceBlock
												? 'rotate(-90deg)'
												: 'rotate(90deg)',
											transition: 'transform 0.3s ease',
										}}
										onClick={() => setExpandedPriceBlock(!expandedPriceBlock)}
									/>
								</CustomToolTip>
								)}
							</div>
						</div>
						<div // ---------------------- pudełko cenę m2 i lokalu -----------------------
							style={{
								width: '100%',
								display: 'grid',
								alignItems: 'center',
								// justifyContent: 'space-between',
								gridTemplateColumns: '1fr 1fr',
								columnGap: '2.5rem',
								padding: '1rem 0',
								borderTop: '1px solid var(--color-labels)',
							}}
						>
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'flex-start',
									// minWidth: '40%',
								}}
							>
								<span className={bim_styles.apartment_tile_data_type}>
									{t('price_of_sqm')}
									<span style={{ color: 'grey', fontSize: '0.75rem' }}>
										{` (${vatRate}% VAT):`}
									</span>
								</span>
								<span className={bim_styles.apartment_tile_data_value}>
									{getDisplayPrice(currentPriceSqm)}
								</span>
								<div
									style={{
										maxHeight: expandedPriceBlock ? '200px' : '0',
										opacity: expandedPriceBlock ? 1 : 0,
										overflow: 'hidden',
										transition:
											'max-height 300ms ease-in-out, opacity 300ms ease-in-out',
										display: 'flex',
										flexDirection: 'column',
									}}
								>
									{(didPriceChange || isOnOffer) && (
										<span
											style={{
												textDecoration: 'line-through',
												color: 'grey',
												fontSize: '0.875rem',
											}}
										>
											{getDisplayPrice(
												priceHistory[0].price_brutto /
													activeApartmentData?.area_original
											)}
										</span>
									)}
									{isOnOffer && (
										<span
											style={{
												color: 'grey',
												fontSize: '0.75rem',
												paddingTop: '0.25rem',
												maxWidth: '10rem',
											}}
										>
											{t('lowest_price_in_30_days')}:
											<br />
											{getDisplayPrice(getLowestPriceIn30DaysBeforeOffer())}
										</span>
									)}
								</div>
							</div>
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'flex-start',
									// minWidth: '40%',
								}}
							>
								<span className={bim_styles.apartment_tile_data_type}>
									{t('price_of_unit')}
									<span style={{ color: 'grey', fontSize: '0.75rem' }}>
										{` (${vatRate}% VAT):`}
									</span>
								</span>

								<span className={bim_styles.apartment_tile_data_value}>
									{getDisplayPrice(currentPrice)}
								</span>
								<div
									style={{
										maxHeight: expandedPriceBlock ? '200px' : '0',
										opacity: expandedPriceBlock ? 1 : 0,
										overflow: 'hidden',
										transition:
											'max-height 300ms ease-in-out, opacity 300ms ease-in-out',
										display: 'flex',
										flexDirection: 'column',
									}}
								>
									{(didPriceChange || isOnOffer) && (
										<span
											style={{
												textDecoration: 'line-through',
												color: 'grey',
												fontSize: '0.875rem',
											}}
										>
											{getDisplayPrice(priceHistory[0].price_brutto)}
										</span>
									)}
									{isOnOffer && (
										<span
											style={{
												color: 'grey',
												fontSize: '0.75rem',
												paddingTop: '0.25rem',
												maxWidth: '10rem',
											}}
										>
											{t('lowest_price_in_30_days')}:
											<br />
											{getDisplayPrice(getLowestPriceIn30DaysBeforeOffer())}
										</span>
									)}
								</div>
							</div>
						</div>
					</div>
					{addedSupplements.length > 0 && (
						<div // ---------------------- pudełko na dodane suplementy -----------------------
							style={{
								width: '100%',
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'flex-start',
								gap: '0',
								padding: '1rem 0',
								borderTop: '1px solid var(--color-labels)',
							}}
						>
							<div
								style={{
									width: '100%',
									maxHeight: expandedPriceBlock ? '2000px' : '0',
									opacity: expandedPriceBlock ? 1 : 0,
									overflow: 'hidden',
									transition:
										'max-height 300ms ease-in-out, opacity 300ms ease-in-out',
								}}
							>
								<span //h6
									style={{
										display: 'block',
										fontWeight: '400',
										fontSize: '0.9rem',
										color: 'var(--color-labels)',
										fontFamily: 'var(--font-headings)',
										paddingBottom: '0.5rem',
									}}
								>
									Dodane części przynależne:
								</span>
								{addedSupplements.filter((sup) => sup.type === 'parking') && (
									<div // ---------------------- pudełko na suplementy parkingu -----------------------
										style={{
											width: '100%',
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'flex-start',
											gap: '0',
											paddingBottom: '1rem',
										}}
									>
										<span //h5
											style={{
												display: 'block',
												fontWeight: '600',
												fontSize: '0.9rem',
												color: 'var(--color-labels)',
												fontFamily: 'var(--font-headings)',
											}}
										>
											Miejsca postojowe
										</span>
										{jointOfferSupplements.map(
											(sup) =>
												sup.type === 'parking' && (
													<div // ---------------------- pudełko na suplement -----------------------
														key={sup.number}
														style={{
															width: '100%',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'space-between',
															gap: '2.5rem',
															wrap: 'nowrap',
															paddingLeft: '0.5rem',
														}}
													>
														<span
															style={{
																display: 'block',
																fontWeight: '400',
																fontSize: '0.75rem',
																color: 'var(--color-labels)',
																// fontFamily: 'var(--font-headings)',
															}}
														>
															{`Miejsce postojowe: ${sup.number}`}
														</span>
														<span
															style={{
																display: 'block',
																fontWeight: '600',
																fontSize: '0.875rem',
																color: 'var(--color-headings)',
																// fontFamily: 'var(--font-headings)',
															}}
														>
															{getDisplaySupplementPrice(sup.priceRange)}
														</span>
													</div>
												)
										)}
										{userAddedSupplements.map(
											(sup) =>
												sup.type === 'parking' && (
													<div // ---------------------- pudełko na suplement -----------------------
														key={sup.number}
														style={{
															width: '100%',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'space-between',
															gap: '2.5rem',
															wrap: 'nowrap',
															paddingLeft: '0.5rem',
														}}
													>
														<span
															style={{
																display: 'block',
																fontWeight: '400',
																fontSize: '0.75rem',
																color: 'var(--color-labels)',
																// fontFamily: 'var(--font-headings)',
															}}
														>
															{numberOfUserAddedParkingSpaces > 1
																? `+ Miejsca postojowe: ${numberOfUserAddedParkingSpaces} szt.`
																: `+ Miejsce postojowe: 1 szt.`}
														</span>
														<span
															style={{
																display: 'block',
																fontWeight: '600',
																fontSize: '0.875rem',
																color: 'var(--color-headings)',
																// fontFamily: 'var(--font-headings)',
															}}
														>
															{getDisplaySupplementPrice([
																sup.priceRange[0] *
																	numberOfUserAddedParkingSpaces,
																sup.priceRange[1] *
																	numberOfUserAddedParkingSpaces,
															])}
														</span>
													</div>
												)
										)}
									</div>
								)}
								{addedSupplements.filter((sup) => sup.type === 'storage') && (
									<div // ---------------------- pudełko na suplementy komórek -----------------------
										style={{
											width: '100%',
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'flex-start',
											gap: '0',
											paddingBottom: '1rem',
										}}
									>
										<span //h5
											style={{
												display: 'block',
												fontWeight: '600',
												fontSize: '0.9rem',
												color: 'var(--color-labels)',
												fontFamily: 'var(--font-headings)',
											}}
										>
											Komórki lokatorskie
										</span>
										{jointOfferSupplements.map(
											(sup, idx) =>
												sup.type === 'storage' && (
													<div // ---------------------- pudełko na suplement -----------------------
														key={sup.name + idx}
														style={{
															width: '100%',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'space-between',
															gap: '2.5rem',
															wrap: 'nowrap',
															paddingLeft: '0.5rem',
														}}
													>
														<span
															style={{
																display: 'block',
																fontWeight: '400',
																fontSize: '0.75rem',
																color: 'var(--color-labels)',
																// fontFamily: 'var(--font-headings)',
															}}
														>
															{`Komórka lokatorska: ${sup.number}`}
														</span>
														<span
															style={{
																display: 'block',
																fontWeight: '600',
																fontSize: '0.875rem',
																color: 'var(--color-headings)',
																// fontFamily: 'var(--font-headings)',
															}}
														>
															{getDisplaySupplementPrice(sup.priceRange)}
														</span>
													</div>
												)
										)}
										{userAddedSupplements.map(
											(sup) =>
												sup.type === 'storage' && (
													<div // ---------------------- pudełko na suplement -----------------------
														key={sup.number}
														style={{
															width: '100%',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'space-between',
															gap: '2.5rem',
															wrap: 'nowrap',
															paddingLeft: '0.5rem',
														}}
													>
														<span
															style={{
																display: 'block',
																fontWeight: '400',
																fontSize: '0.75rem',
																color: 'var(--color-labels)',
																// fontFamily: 'var(--font-headings)',
															}}
														>
															{numberOfUserAddedStorageUnits > 1
																? `+ Komórki lokatorskie: ${numberOfUserAddedStorageUnits} szt.`
																: `+ Komórka lokatorska: 1 szt.`}
														</span>
														<span
															style={{
																display: 'block',
																fontWeight: '600',
																fontSize: '0.875rem',
																color: 'var(--color-headings)',
																// fontFamily: 'var(--font-headings)',
															}}
														>
															{getDisplaySupplementPrice([
																sup.priceRange[0] *
																	numberOfUserAddedStorageUnits,
																sup.priceRange[1] *
																	numberOfUserAddedStorageUnits,
															])}
														</span>
													</div>
												)
										)}
									</div>
								)}
							</div>
							<div // ---------------------- pudełko na sume suplementów -----------------------
								style={{
									width: '100%',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									gap: '2.5rem',
									wrap: 'nowrap',
									// paddingTop: '1rem',
									paddingBottom: '1rem',

									cursor: 'pointer',
									border: 'none',
									background: 'none',
								}}
								// onClick={() => {
								// 	setExpandedPriceBlock(!expandedPriceBlock);
								// 	console.log('expandedPriceBlock', expandedPriceBlock);
								// }}
							>
								<span //h6
									style={{
										display: 'block',
										fontWeight: '400',
										fontSize: '0.9rem',
										color: 'var(--color-labels)',
										fontFamily: 'var(--font-headings)',
									}}
								>
									Łączna cena dodanych części
								</span>
								<span
									style={{
										display: 'block',
										fontWeight: '600',
										fontSize: '0.875rem',
										color: 'var(--color-headings)',
										// fontFamily: 'var(--font-headings)',
									}}
								>
									{areAddedSupplementsPriceRanged ? 'od ' : ''}
									{getDisplayPrice(sumSupsPrice, 0)}
								</span>
							</div>
							<div // ---------------------- pudełko na sume całościową -----------------------
								style={{
									width: '100%',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									gap: '2.5rem',
									wrap: 'nowrap',
									borderTop: '1px solid var(--color-labels)',
									paddingTop: '1rem',
								}}
							>
								<span //h5
									style={{
										display: 'block',
										fontWeight: '600',
										fontSize: '0.9rem',
										color: 'var(--color-labels)',
										fontFamily: 'var(--font-headings)',
									}}
								>
									Łączna cena nieruchomości
								</span>
								<span
									style={{
										display: 'block',
										fontWeight: '600',
										fontSize: '1.2rem',
										color: 'var(--color-headings)',
										textWrap: 'nowrap',
										// fontFamily: 'var(--font-headings)',
									}}
								>
									{areAddedSupplementsPriceRanged ? 'od ' : ''}
									{getDisplayPrice(sumSupsPrice + currentPrice, 2)}
								</span>
							</div>
						</div>
					)}
					<div className={bim_styles.apartment_tile_data_element}>
						{/* <div style={{ display: 'flex', justifyContent: 'space-between' }}>
						<span className={bim_styles.apartment_tile_data_type}>
							{t('price')}
							<span style={{ color: 'grey', fontSize: '0.75rem' }}>
								{` (${vatRate}% VAT)`}
							</span>
						</span>
					</div> */}
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '0',
								justifyContent: 'flex-start',
							}}
						>
							{/* {(didPriceChange || isOnOffer) && (
							<span
								style={{
									textDecoration: 'line-through',
									color: 'grey',
									fontSize: '0.875rem',
								}}
							>
								{`${getDisplayPrice(
									priceHistory[0].price_brutto
								)} / ${getDisplayPrice(
									priceHistory[0].price_brutto /
										activeApartmentData?.area_original
								)} m2`}
							</span>
						)} */}
							{/* <div
							style={{
								display: 'flex',
								justifyContent: 'space-between',
								alignContent: 'center',
								alignItems: 'center',
								padding: '0.5rem 0',
							}}
						>
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'flex-start',
									gap: '1.5rem',
								}}
							>
								<span className={bim_styles.apartment_tile_data_value}>
									{getDisplayPrice(currentPrice)}
								</span>
								<span
									style={{
										color: 'grey',
										fontSize: '0.875rem',
										textTransform: 'italic',
									}}
								>
									{getDisplayPrice(currentPriceSqm)}
									{`/m2`}
								</span>
							</div>
							<CustomToolTip
								title={
									<>
										<p className={apart_styles.infoTooltip__paragraph}>
											{t('price_history')}
										</p>
										<div
											style={{
												display: 'grid',
												gridTemplateColumns: 'repeat(3, minmax(6rem, 1fr))',
												columnGap: '0.5rem',
											}}
										>
											<div>{t('price_of_sqm')}:</div>
											<div>{t('price_total')}:</div>
											<div>{t('price_since')}:</div>
											{priceHistoryWithSqm.map((entry, index) => {
												const date = entry.date
													? new Date(entry.date).toLocaleString('pl-PL', {
															year: 'numeric',
															month: '2-digit',
															day: '2-digit',
													  })
													: '-';
												return (
													<>
														<div key={index * priceHistoryWithSqm.length}>
															{getDisplayPrice(entry.price_m2)}
														</div>
														<div key={index * priceHistoryWithSqm.length + 1}>
															{getDisplayPrice(entry.price_brutto)}
														</div>
														<div key={index * priceHistoryWithSqm.length + 2}>
															{date}
														</div>
													</>
												);
											})}
										</div>
									</>
								}
								slots={{
									transition: Zoom,
								}}
								arrow
							>
								<IconHistory style={{ width: '1.5rem', height: '1.5rem' }} />
							</CustomToolTip>
						</div> */}
							{/* {isOnOffer && (
							<span style={{ color: 'grey', fontSize: '0.75rem' }}>
								{t('lowest_price_in_30_days')}:
								<br />
								{getDisplayPrice(getLowestPriceIn30DaysBeforeOffer())}
							</span>
						)} */}
							{/* <div
							style={{ marginTop: '0.5rem', color: 'var(--color-headings)' }}
						>
							<span>Dodaj powierzchnie dodatkowe</span>
							<div style={{ display: 'flex', gap: '0.5rem' }}>
								<div
									className={`${bim_styles.filter_container} ${bim_styles.filter_buttons}`}
								>
									<Select
										styles={customStyles}
										value={parkingSpacesOptions.filter((opt) =>
											selectedParkingSpaces.includes(opt.value)
										)}
										options={parkingSpacesOptions}
										onChange={(selectedOptions) => {
											const selected = selectedOptions
												? selectedOptions.map((opt) => opt.value)
												: [];
											setSelectedParkingSpaces(selected);
										}}
										className={bim_styles.multiselect}
										placeholder={`miejsca postojowe`}
									/>
								</div>
								<div
									className={`${bim_styles.filter_container} ${bim_styles.filter_buttons}`}
								>
									<Select
										value={storageUnitOptions.filter((opt) =>
											selectedStorageUnits.includes(opt.value)
										)}
										options={storageUnitOptions}
										onChange={(selectedOptions) => {
											const selected = selectedOptions
												? selectedOptions.map((opt) => opt.value)
												: [];
											setSelectedStorageUnits(selected);
										}}
										className={bim_styles.multiselect}
										placeholder={`komórki lokatorskie`}
									/>
								</div>
							</div>
						</div> */}
						</div>
					</div>
				</div>
				<ApartmentQueryButton isStuck={isStuck}/>
			</div>
		</>
	);
}

export default PriceInfoBlock;
